import { Platform, Linking } from 'react-native';

// Native share/download modules (absent on web — handled per-platform below).
let Sharing = null;
let FileSystem = null;
try {
  Sharing = require('expo-sharing');
  FileSystem = require('expo-file-system/legacy');
} catch {
  // web
}

const sanitize = (name) => (name || '').replace(/[^\w.\- ]+/g, '').trim();

// Derive a reasonable filename from a URL when the caller has none (e.g. a photo
// whose only metadata is its download URL).
const nameFromUrl = (url, fallbackExt = 'jpg') => {
  try {
    const path = decodeURIComponent(url.split('?')[0]);
    const last = path.substring(path.lastIndexOf('/') + 1);
    const clean = sanitize(last);
    if (clean && clean.includes('.')) return clean;
    if (clean) return `${clean}.${fallbackExt}`;
  } catch {}
  return `file.${fallbackExt}`;
};

// Let the user save or send a stored file. On native this opens the OS share
// sheet (Save to Files, Save Image, Mail, Messages, …); on web it downloads the
// file (falling back to opening it in a new tab for cross-origin links). The URL
// stays valid, so this works for anything filed on a horse — photos, health
// certificates, invoices, statements. Resolves to how it was handled.
export const shareOrDownloadFile = async ({ url, fileName, mime } = {}) => {
  if (!url) return 'error';
  const isImage = (mime || '').startsWith('image/');
  const name = sanitize(fileName) || nameFromUrl(url, isImage ? 'jpg' : (mime === 'application/pdf' ? 'pdf' : 'dat'));

  if (Platform.OS === 'web') {
    if (typeof document !== 'undefined') {
      const a = document.createElement('a');
      a.href = url;
      a.download = name;      // honored for same-origin; cross-origin opens in a tab
      a.target = '_blank';
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
      return 'downloaded';
    }
    if (typeof window !== 'undefined') { window.open(url, '_blank'); return 'opened'; }
    return 'error';
  }

  // Native: download to the cache, then present the share sheet.
  if (FileSystem && Sharing && (await Sharing.isAvailableAsync())) {
    const target = FileSystem.cacheDirectory + name;
    const { uri } = await FileSystem.downloadAsync(url, target);
    await Sharing.shareAsync(uri, {
      mimeType: mime || undefined,
      dialogTitle: 'Share or save',
      UTI: mime === 'application/pdf' ? 'com.adobe.pdf' : undefined,
    });
    return 'shared';
  }

  // Last resort: hand off to the OS to open the link.
  Linking.openURL(url);
  return 'opened';
};
